import { useState, ChangeEventHandler } from "react";
import produce from "immer";
import { Trans, useTranslation } from "react-i18next";
import { Box, Grid, MenuItem, TextField, Typography } from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import SelectField from "components/Forms/SelectField";
import AutocompleteSearchInput from "components/AutocompleteSearchInput";
import { ErrorMessage } from "../../Actions/Styled";
import { Field, FormFields } from "../../../../common/FormFields";

import { useRuleContext } from "../../../context/ruleContext";

import { NOTIFICATION_LIMITS } from "@dashboard-v3/shared";
import useNotificationVariables from "pages/Rules/hooks/useNotificationVariables";

import { TFunction } from "i18next";
import { ApprovalSteps, Reminder, SenderNotification } from "@dashboard-v3/api";
import { getApprovalSteps } from "pages/Rules/Form/context/ruleSelectors";

type FormUpdate = Partial<{
  notification: Partial<SenderNotification>;
  reminder: Partial<Reminder>;
}>;

export default function ReminderNotificationSettings() {
  const { variables, replaceIdsWithLabels, replaceLabelsWithIds } =
    useNotificationVariables({
      ruleType: "REQUEST_APPROVAL",
      notificationType: "REMINDER",
    });
  const { t } = useTranslation("rules");
  const { state, dispatch } = useRuleContext();
  const steps = getApprovalSteps(state.rule);
  const [isEnabled, setIsEnabled] = useState(initEnabled(steps));
  const { reminder, reminderSenderNotification, expiration } = steps;
  const { minInterval, notificationCount } = getMinReminderInterval();
  const recipient = t("approvalSteps.recipientType.reminder");
  const intervalError =
    hasError("REMINDER_INTERVAL_EXCEEDED") ||
    hasError("REMINDER_INTERVAL_MIN_ERROR");
  const titleError = hasError(
    "REMINDER_TITLE_REQUIRED",
    "REMINDER_TITLE_MAX_LENGTH"
  );
  const bodyError = hasError(
    "REMINDER_BODYTEXT_REQUIRED",
    "REMINDER_BODYTEXT_MAX_LENGTH"
  );
  const accessLinkError = hasError("REMINDER_ACCESSLINK_FIELD_ERROR");
  const cancelLinkError = hasError("REMINDER_CANCELLINK_FIELD_ERROR");
  const linkLabelDefaults = {
    accessLinkLabel: t("approvalSteps.senderEmail.access"),
    cancelLinkLabel: t("approvalSteps.senderEmail.cancel"),
  };
  const intervalSubtitleValues = {
    minInterval,
    notificationCount,
    expiration: expiration.value,
    unit: getTimeUnit(expiration.type, t),
  };

  const updateSettings = (update: FormUpdate) => {
    const { reminder, notification } = update;
    const isDisabling = reminder === null || notification === null;

    if (isDisabling) {
      return dispatch({
        type: "updateStep",
        payload: {
          step: "approvalSteps",
          changes: { reminder: null, reminderSenderNotification: null },
        },
      });
    }

    dispatch({
      type: "updateStep",
      payload: {
        step: "approvalSteps",
        changes: {
          reminder: { ...steps.reminder, ...reminder },
          reminderSenderNotification: {
            ...steps.reminderSenderNotification,
            ...notification,
          },
        },
      },
    });
  };

  const handleNotificationText: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, value } = e.target;
    const update = produce(steps.reminderSenderNotification, draft => {
      draft[name] = value;
    });

    updateSettings({ notification: update });
  };

  function handleNotificationBody(value: string) {
    const update = produce(steps.reminderSenderNotification, draft => {
      draft.bodyText = replaceLabelsWithIds(value);
    });

    updateSettings({ notification: update });
  }

  const handleNotificationLinks: ChangeEventHandler<HTMLInputElement> = e => {
    const { name, checked } = e.target;
    const update = produce(reminderSenderNotification, draft => {
      const linkLabelKey = `${name}Label`;

      draft[name] = checked;

      if (checked && !draft[linkLabelKey]) {
        draft[linkLabelKey] = linkLabelDefaults[linkLabelKey];
      }

      if (!checked) {
        draft[linkLabelKey] = "";
      }
    });

    updateSettings({ notification: update });
  };

  const onEnableNotification: ChangeEventHandler<HTMLInputElement> = e => {
    const { checked } = e.target;
    setIsEnabled(checked);

    if (!checked) return updateSettings({ notification: null, reminder: null });

    const notification = {
      accessLink: false,
      accessLinkLabel: t("approvalSteps.senderEmail.access"),
      cancelLink: true,
      cancelLinkLabel: t("approvalSteps.senderEmail.cancel"),
      title: t("approvalSteps.senderReminder.defaultTitle"),
      bodyText: t("approvalSteps.senderReminder.defaultBody"),
    };

    const reminder = {
      type: "DAYS" as Reminder["type"],
      value: 7,
    };

    updateSettings({ notification, reminder });
  };

  function hasError(...errorCodes: string[]) {
    const errors = (state.steps.approvalSteps?.error || []) as string[];

    return errors.some(error => errorCodes.includes(error));
  }

  function getMinReminderInterval() {
    const { type, value } = expiration;
    const expirationHours = type === "HOURS" ? value : value * 24;
    let minInterval: number;

    if (expirationHours < 24) {
      minInterval = 2;
    } else if (expirationHours >= 24 && expirationHours <= 72) {
      minInterval = 6;
    } else if (expirationHours >= 72 && expirationHours <= 168) {
      minInterval = 12;
    } else {
      minInterval = 48;
    }

    const reminderHours =
      reminder?.type === "HOURS" ? reminder.value : (reminder?.value || 0) * 24;

    const notificationCount =
      reminderHours > 0 ? Math.floor(expirationHours / reminderHours) : 0;

    return {
      minInterval,
      notificationCount,
    };
  }

  function getIntervalError() {
    const { error = [] } = state.steps.approvalSteps;
    const expirationType = getTimeUnit(expiration.type, t);
    const reminderType = getTimeUnit(reminder.type, t);

    if (error.includes("REMINDER_INTERVAL_EXCEEDED")) {
      return t("approvalSteps.reminderInterval.exceedExpiration", {
        reminderValue: reminder.value,
        expirationValue: expiration.value,
        reminderType,
        expirationType,
      });
    }

    if (error.includes("REMINDER_INTERVAL_MIN_ERROR")) {
      return t("approvalSteps.reminderInterval.minError", {
        expirationValue: expiration.value,
        expirationType,
        minInterval,
      });
    }
  }

  return (
    <Box mb="25px" mt="9px">
      <SwitchField
        label={t("approvalSteps.enableReminderLabel")}
        value={isEnabled}
        onChange={onEnableNotification}
        style={{ marginBottom: isEnabled ? 10 : 0 }}
        description={!isEnabled && t("approvalSteps.enableReminderDesc")}
      />
      {isEnabled && (
        <Box padding="8px 16px 16px">
          <FormFields>
            <Box style={{ maxWidth: "90%" }}>
              <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                {t("approvalSteps.reminderInterval.title")}
              </Typography>
              <Typography
                display="block"
                variant="caption"
                color="textSecondary"
              >
                <Trans
                  t={t}
                  i18nKey="approvalSteps.reminderInterval.subtitle"
                  values={intervalSubtitleValues}
                >
                  <Typography
                    variant="caption"
                    color="primary"
                    style={{ fontWeight: 500 }}
                  ></Typography>
                  <Typography
                    variant="caption"
                    color="primary"
                    style={{ fontWeight: 500 }}
                  ></Typography>
                  <Typography
                    variant="caption"
                    color="primary"
                    style={{ fontWeight: 500 }}
                  ></Typography>
                </Trans>
              </Typography>
              <Grid container spacing={2} style={{ marginTop: 10 }}>
                <Grid item lg={6}>
                  <TextField
                    inputProps={{ min: 1 }}
                    fullWidth
                    name="intervalValue"
                    type="number"
                    variant="outlined"
                    label={t("approvalSteps.expiration.durationLabel")}
                    value={String(reminder?.value || 0)}
                    error={intervalError}
                    onChange={e =>
                      updateSettings({
                        reminder: { value: Number(e.target.value) },
                      })
                    }
                  />
                </Grid>

                <Grid item lg={6}>
                  <SelectField<Reminder["type"]>
                    name="intervalType"
                    label={t("approvalSteps.expiration.unitLabel")}
                    options={["DAYS", "HOURS"]}
                    value={reminder?.type || ""}
                    error={intervalError}
                    onChange={e =>
                      updateSettings({
                        reminder: { type: e.target.value as Reminder["type"] },
                      })
                    }
                    renderOption={opt => (
                      <MenuItem key={opt} value={opt}>
                        {t(`approvalSteps.reminderUnit.${opt}`) as string}
                      </MenuItem>
                    )}
                  />
                </Grid>
              </Grid>
              <ErrorMessage
                isError={intervalError}
                wording={getIntervalError()}
              />
            </Box>

            <Field>
              <TextField
                name="title"
                variant="outlined"
                label={t("approvalSteps.title", { recipient })}
                error={titleError}
                value={reminderSenderNotification?.title || ""}
                onChange={handleNotificationText}
                inputProps={{ maxLength: NOTIFICATION_LIMITS.TITLE }}
              />
              <ErrorMessage
                isError={titleError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>
            <Field>
              <Typography
                variant="caption"
                color={bodyError ? "error" : "textSecondary"}
              >
                {t("approvalSteps.bodyText", { recipient })}
              </Typography>
              <AutocompleteSearchInput
                name="bodyText"
                multiple
                rows={5}
                fullWidth
                variables={variables}
                error={bodyError}
                maxLength={NOTIFICATION_LIMITS.BODY_TEXT}
                onChange={handleNotificationBody}
                value={replaceIdsWithLabels(
                  reminderSenderNotification?.bodyText || ""
                )}
              />
              <ErrorMessage
                isError={bodyError}
                wording={t("approvalSteps.fieldRequired")}
              />
            </Field>
            <SwitchField
              name="accessLink"
              label={t("approvalSteps.accessLink", { recipient })}
              description={t("approvalSteps.accessLinkDesc")}
              value={reminderSenderNotification?.accessLink || false}
              onChange={handleNotificationLinks}
            />
            {reminderSenderNotification.accessLink && (
              <Field>
                <TextField
                  name="accessLinkLabel"
                  variant="outlined"
                  label={t("approvalSteps.accessLinkLabel")}
                  value={reminderSenderNotification?.accessLinkLabel || ""}
                  onChange={handleNotificationText}
                  error={accessLinkError}
                  inputProps={{ maxLength: NOTIFICATION_LIMITS.CTA_LABEL }}
                />
                <ErrorMessage
                  isError={accessLinkError}
                  wording={t("approvalSteps.fieldRequired")}
                />
              </Field>
            )}

            <SwitchField
              name="cancelLink"
              label={t("approvalSteps.cancelLink", { recipient })}
              description={t("approvalSteps.cancelLinkDesc")}
              value={reminderSenderNotification?.cancelLink || false}
              onChange={handleNotificationLinks}
            />
            {reminderSenderNotification.cancelLink && (
              <Field>
                <TextField
                  name="cancelLinkLabel"
                  variant="outlined"
                  label={t("approvalSteps.cancelLinkLabel")}
                  value={reminderSenderNotification?.cancelLinkLabel || ""}
                  onChange={handleNotificationText}
                  error={cancelLinkError}
                  inputProps={{ maxLength: NOTIFICATION_LIMITS.CTA_LABEL }}
                />
                <ErrorMessage
                  isError={cancelLinkError}
                  wording={t("approvalSteps.fieldRequired")}
                />
              </Field>
            )}
          </FormFields>
        </Box>
      )}
    </Box>
  );
}

function initEnabled(approvalSteps: ApprovalSteps) {
  const { reminderSenderNotification, reminder } = approvalSteps;
  return () => Boolean(reminderSenderNotification) && Boolean(reminder);
}

const getTimeUnit = (unit: string, t: TFunction) => {
  const timeUnit = t(`approvalSteps.reminderUnit.${unit}`);
  return timeUnit.toLowerCase();
};
